import React, { useState, useEffect } from 'react';
import ColorInit from "../helper/ColorInit";
import HeaderOne from "./HeaderTwo";
import BottomFooter from "./BottomFooter";

const TermsAndConditions = () => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    introduction: true,
    acceptance: false,
    user_accounts: false,
    user_responsibilities: false,
    prohibited_activities: false,
    intellectual_property: false,
    user_generated_content: false,
    third_party_links: false,
    payment_terms: false,
    refunds: false,
    limitation_liability: false,
    disclaimer: false,
    indemnification: false,
    account_termination: false,
    modifications: false,
    governing_law: false,
    dispute_resolution: false,
    severability: false,
    entire_agreement: false,
    contact_information: false
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/terms/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch terms: ${response.status}`);
      }

      const data = await response.json();
      setTerms(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error('Error fetching terms:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleAllSections = (expand) => {
    const allSections = Object.keys(expandedSections).reduce((acc, key) => {
      acc[key] = expand;
      return acc;
    }, {});
    setExpandedSections(allSections);
  };

  const renderList = (listString) => {
    if (!listString) return null;
    return listString.split('\n').filter(item => item.trim()).map((item, index) => (
      <li key={index} style={{ marginBottom: "10px", color: "#6B7280", paddingLeft: "10px" }}>{item.trim()}</li>
    ));
  };

  const palette = {
    lightMint: "#C3DCD8",
    softGrey: "#DBD9DA",
    mutedGold: "#C5BD96",
    mutedTeal: "#93B0AC",
    bgWhite: "#F9FAFB"
  };

  const cardStyle = (expanded) => ({
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    marginBottom: "24px",
    border: `2px solid ${palette.lightMint}`,
    boxShadow: "0 10px 30px rgba(147, 176, 172, 0.1)",
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    cursor: "pointer",
    position: "relative"
  });

  const headerStyle = (expanded) => ({
    backgroundColor: expanded ? "#FFFFFF" : palette.lightMint,
    padding: "24px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "background-color 0.3s ease"
  });

  const titleStyle = (expanded) => ({
    color: expanded ? palette.mutedTeal : "#555",
    margin: "0",
    fontSize: "1.35rem",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "15px"
  });

  const contentStyle = (expanded) => ({
    padding: expanded ? "32px" : "0",
    maxHeight: expanded ? "5000px" : "0",
    opacity: expanded ? "1" : "0",
    overflow: "hidden",
    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    color: "#4B5563",
    fontSize: "16px",
    lineHeight: "1.8"
  });

  const buttonStyle = {
    backgroundColor: palette.mutedTeal,
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 15px rgba(147, 176, 172, 0.2)"
  };

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div style={{ textAlign: "center", padding: "100px", backgroundColor: palette.bgWhite, minHeight: "60vh" }}>
          <p style={{ fontSize: "1.5rem", color: palette.mutedTeal }}>Loading terms and conditions...</p>
        </div>
        <BottomFooter />
      </>
    );
  }

  if (error || !terms) {
    return (
      <>
        <HeaderOne />
        <div style={{ textAlign: "center", padding: "100px", backgroundColor: palette.bgWhite, minHeight: "60vh" }}>
          <p style={{ fontSize: "1.5rem", color: palette.mutedTeal }}>{error || "No terms available."}</p>
        </div>
        <BottomFooter />
      </>
    );
  }

  const sections = [
    { id: 'introduction', title: terms.intro_title || "1. Introduction", text: terms.intro_text },
    { id: 'acceptance', title: terms.acceptance_title, text: terms.acceptance_text },
    {
      id: 'user_accounts',
      title: terms.accounts_title,
      subsections: [
        { title: terms.accounts_sub1_title, text: terms.accounts_sub1_text },
        { title: terms.accounts_sub2_title, text: terms.accounts_sub2_text }
      ]
    },
    { id: 'user_responsibilities', title: terms.responsibilities_title, text: terms.responsibilities_intro, list: terms.responsibilities_list },
    { id: 'prohibited_activities', title: terms.prohibited_title, text: terms.prohibited_intro, list: terms.prohibited_list },
    { id: 'intellectual_property', title: terms.ip_title, text: terms.ip_text1, extra: terms.ip_text2 },
    {
      id: 'user_generated_content',
      title: terms.ugc_title,
      subsections: [
        { title: terms.ugc_sub1_title, text: terms.ugc_sub1_text },
        { title: terms.ugc_sub2_title, text: terms.ugc_sub2_text }
      ]
    },
    { id: 'third_party_links', title: terms.third_party_title, text: terms.third_party_text },
    { id: 'payment_terms', title: terms.payment_title, text: terms.payment_text1, extra: terms.payment_text2 },
    { id: 'refunds', title: terms.refund_title, text: terms.refund_text },
    { id: 'limitation_liability', title: terms.liability_title, text: terms.liability_text, list: terms.liability_list },
    { id: 'disclaimer', title: terms.disclaimer_title, text: terms.disclaimer_text },
    { id: 'indemnification', title: terms.indemnification_title, text: terms.indemnification_text },
    { id: 'account_termination', title: terms.termination_title, text: terms.termination_text1, extra: terms.termination_text2 },
    { id: 'modifications', title: terms.modifications_title, text: terms.modifications_text },
    { id: 'governing_law', title: terms.law_title, text: terms.law_text },
    { id: 'dispute_resolution', title: terms.dispute_title, text: terms.dispute_text },
    { id: 'severability', title: terms.severability_title, text: terms.severability_text },
    { id: 'entire_agreement', title: terms.entire_title, text: terms.entire_text },
    {
      id: 'contact_information',
      title: terms.contact_title,
      text: terms.contact_text,
      isContact: true,
      contactInfo: {
        email: terms.contact_email,
        phone: terms.contact_phone,
        address: terms.contact_address
      },
      footer: terms.footer_text
    }
  ].filter(s => s.title);

  return (
    <>
      <HeaderOne />
      <ColorInit color={true} />

      <div style={{ backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
        {/* Hero Section */}
        <section style={{
          background: "linear-gradient(135deg, #1D2A3A 0%, #2C3E50 100%)",
          padding: "80px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          marginBottom: "40px"
        }}>
          <div className="hero-glow" style={{
            position: "absolute",
            top: "-50%",
            left: "-20%",
            width: "140%",
            height: "200%",
            background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            pointerEvents: "none"
          }}></div>

          <div style={{ maxWidth: "1000px", margin: "0 auto", position: "relative", zIndex: 1 }}>
            <h1 style={{
              fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
              fontWeight: "800",
              marginBottom: "20px",
              color: "white",
              textShadow: "0 2px 10px rgba(0,0,0,0.1)"
            }}>
              {terms.page_title || "Terms and Conditions"}
            </h1>
            <p style={{
              fontSize: "1.25rem",
              color: "white",
              opacity: "0.95",
              lineHeight: "1.6",
              maxWidth: "700px",
              margin: "0 auto"
            }}>
              Last Updated: {terms.last_updated}
            </p>
          </div>
        </section>

        <div style={{ maxWidth: "1100px", margin: "20px auto 0", padding: "0 20px 100px", position: "relative", zIndex: 3 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px", flexWrap: "wrap", gap: "20px" }}>
            <div style={{
              color: palette.mutedTeal,
              fontSize: "2.2rem",
              fontWeight: "700",
              paddingLeft: "10px",
              borderLeft: `8px solid ${palette.lightMint}`
            }}>
              Terms Details
            </div>
            <div style={{ display: "flex", gap: "15px" }}>
              <button
                onClick={() => toggleAllSections(true)}
                style={buttonStyle}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >
                Expand All
              </button>
              <button
                onClick={() => toggleAllSections(false)}
                style={{ ...buttonStyle, backgroundColor: palette.lightMint, color: palette.mutedTeal }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Section Builder */}
          {sections.map((section) => (
            <div
              key={section.id}
              style={cardStyle(expandedSections[section.id])}
              onClick={() => toggleSection(section.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(147, 176, 172, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(147, 176, 172, 0.1)";
              }}
            >
              <div style={headerStyle(expandedSections[section.id])}>
                <h3 style={titleStyle(expandedSections[section.id])}>
                  <span style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    backgroundColor: expandedSections[section.id] ? palette.lightMint : "#FFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease"
                  }}>
                    <i className={`ph ph-caret-${expandedSections[section.id] ? 'up' : 'down'}`} style={{ fontSize: "18px", color: palette.mutedTeal }}></i>
                  </span>
                  {section.title}
                </h3>
              </div>
              <div style={contentStyle(expandedSections[section.id])}>
                {section.text && <p style={{ marginBottom: "15px" }}>{section.text}</p>}
                {section.extra && <p style={{ marginBottom: "15px" }}>{section.extra}</p>}

                {section.subsections && section.subsections.map((sub, i) => (
                  <div key={i} style={{ marginBottom: "25px" }}>
                    {sub.title && <h4 style={{ color: palette.mutedGold, marginBottom: "10px", fontSize: "1.2rem", fontWeight: "700" }}>{sub.title}</h4>}
                    {sub.text && <p>{sub.text}</p>}
                  </div>
                ))}

                {section.list && <ul style={{ listStyleType: "none", padding: 0, marginTop: "20px" }}>{renderList(section.list)}</ul>}

                {section.isContact && (
                  <div style={{ marginTop: "40px", padding: "30px", borderTop: `2px solid ${palette.bgWhite}`, textAlign: "center" }}>
                    <div style={{
                      backgroundColor: palette.bgWhite,
                      padding: "25px",
                      borderRadius: "15px",
                      marginBottom: "25px"
                    }}>
                      {section.contactInfo.email && <p><strong>Email:</strong> {section.contactInfo.email}</p>}
                      {section.contactInfo.phone && <p><strong>Phone:</strong> {section.contactInfo.phone}</p>}
                      {section.contactInfo.address && <p><strong>Address:</strong> {section.contactInfo.address}</p>}
                    </div>
                    {section.footer && <p style={{ fontSize: "1rem", color: "#6B7280", fontStyle: "italic" }}>{section.footer}</p>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <BottomFooter />
    </>
  );
};

export default TermsAndConditions;